import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { isRealtimeEnabled, subscribeToStation } from '../../lib/realtime';
import { observeServerTime } from './sync';
import { rooms, type RoomView } from './api';

/**
 * How close to the end of a track counts as "the boundary".
 *
 * Wide enough to cover a poll landing just before it and the next one just
 * after, so the rollover is never missed by a fraction of a second.
 */
const BOUNDARY_MS = 10_000;

/** How often a listener says they are still here. Half the server's timeout. */
const HEARTBEAT_MS = 30_000;

/**
 * A room, kept current.
 *
 * ## Three mechanisms, and why all three
 *
 * **The socket** is the fast path: a skip reaches every listener in a few
 * milliseconds. It is not the only path, because §8.2 is explicit that the
 * socket is a delivery mechanism and never a source of truth - a listener whose
 * connection dropped ten seconds ago has no idea, and would otherwise sit on a
 * finished track for ever.
 *
 * **The poll** is the backstop. Slow on purpose while a socket is connected -
 * it exists to heal a missed event, not to deliver them - and fast when there
 * is no socket at all, which is also the development default when Soketi is not
 * running.
 *
 * **The heartbeat** is what makes the listener count real, and is on its own
 * timer because it is a write, not a read, and must keep going even while the
 * tab is idle and the query is not refetching.
 *
 * ## Every response feeds the clock
 *
 * `observeServerTime` on each fetch, so the offset estimate keeps improving
 * while somebody listens. It is a median over the last several samples, so one
 * request that queued behind a slow one cannot move it.
 */
/**
 * A broadcast, kept current.
 *
 * Addressed by the host's username, because that is where a broadcast lives -
 * there is no room id in any URL. `mine` switches to the console's own endpoint,
 * which additionally creates the station on first use.
 */
export function useRoom(
  username: string | undefined,
  options: {
    listen?: boolean;
    mine?: boolean;
    enabled?: boolean;
    /**
     * Refetch on mount rather than trusting what is cached.
     *
     * For the one caller that starts *because* something changed - see
     * `RoomFollower`. Polling stops while a room is offline, so a cached
     * "offline" is a dead end: nothing would ever ask again, and a room that
     * came back on air would never be noticed.
     */
    fresh?: boolean;
  } = {},
) {
  const queryClient = useQueryClient();
  const key = options.mine ? ['room', 'mine'] : ['room', username];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const view = options.mine ? await rooms.mine() : await rooms.live(username!);

      // Before anything renders: a component reading the playhead in the same
      // tick should already have the corrected clock.
      // Null for somebody who has never hosted, which is most profiles.
      if (view.room) observeServerTime(view.room.server_time);

      return view;
    },
    /*
     * `enabled` is an opt-*out*, defaulting to on.
     *
     * The studio needs the host's own room from the app shell, where it is
     * mounted for everybody - so it has to be able to say "not yet". Without
     * this, every signed-in user would poll `/me/station` from every page on
     * the site whether or not they have ever hosted anything.
     */
    enabled: (options.enabled ?? true) && (options.mine || Boolean(username)),
    /*
     * The interval tightens as a track approaches its end.
     *
     * The server advances a finished room *on read* - nothing else knows a
     * track ended, and a client claiming it did cannot be trusted with
     * everybody else's room. That makes the poll the thing that triggers the
     * rollover, so its interval is the listener's silence at every track
     * boundary. At fifteen seconds that was up to fifteen seconds of nothing,
     * which is what this fixes.
     *
     * So: near the end, poll every second and the room turns over promptly. In
     * the middle of a five-minute track there is nothing to find, and a socket
     * would have delivered anything that did happen.
     */
    /*
     * `always` for followers, so tuning in re-asks.
     *
     * Left alone otherwise: a profile page opening with the cached room and
     * correcting a moment later is fine, and refetching every room view on
     * every mount is a request per navigation for information that is usually
     * unchanged.
     */
    refetchOnMount: options.fresh ? 'always' : undefined,

    refetchInterval: (q) => {
      const view = q.state.data as RoomView | undefined;

      if (view?.room?.status !== 'live') return false;

      const idle = isRealtimeEnabled() ? 15_000 : 3_000;
      const { now_playing: track, position_ms, paused } = view.room;

      if (!track?.duration_ms || position_ms === null || paused) return idle;

      // From the last known position, not a live one - this runs on a timer and
      // does not need to be exact, only early.
      const remaining = track.duration_ms - position_ms;

      return remaining < BOUNDARY_MS ? 1_000 : Math.min(idle, Math.max(1_000, remaining - BOUNDARY_MS));
    },
    // The room keeps playing while the tab is hidden, so it keeps polling too.
    refetchIntervalInBackground: true,
  });

  const stationId = query.data?.room?.id;

  /* ---------------------------------------------------------------------- */
  /* The socket                                                             */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (stationId === undefined) return;

    return subscribeToStation(stationId, (event, payload) => {
      /*
       * A state event carries the playhead, so it is written straight into the
       * cache rather than triggering a refetch. The whole point of pushing it
       * is to avoid a round trip at the moment the track changes - refetching
       * here would put one back.
       *
       * The queue and chat events carry nothing the cache can merge safely, so
       * they invalidate: reconciling a list from two sources is how the two
       * drift apart.
       */
      if (event === 'station.state' && payload) {
        observeServerTime((payload as { server_time: string }).server_time);

        queryClient.setQueryData(key, (previous: RoomView | undefined) =>
          previous ? { ...previous, room: { ...previous.room, ...payload } } : previous,
        );

        return;
      }

      void queryClient.invalidateQueries({ queryKey: key });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, username, options.mine, queryClient]);

  /* ---------------------------------------------------------------------- */
  /* The heartbeat                                                          */
  /* ---------------------------------------------------------------------- */
  const live = query.data?.room?.status === 'live';
  const beating = useRef(false);

  useEffect(() => {
    if (!options.listen || !live || username === undefined) return;

    beating.current = true;

    // Immediately, then on the interval: somebody who opens a room and closes
    // it after ten seconds should still have been counted.
    const beat = () => {
      if (beating.current) void rooms.heartbeat(username).catch(() => {});
    };

    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);

    return () => {
      beating.current = false;
      clearInterval(timer);

      /*
       * Best-effort. Browsers routinely skip this on a hard close, which is
       * exactly why the server sweeps stale rows rather than trusting it - but
       * when it does arrive the count drops promptly instead of ninety seconds
       * later.
       */
      void rooms.leave(username).catch(() => {});
    };
  }, [options.listen, live, username]);

  return query;
}
